import { useState, useEffect } from 'react';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import { Plus, Loader2, TrendingUp, MoreHorizontal, Pencil, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ExportImportButtons } from '../components/ExportImportButtons';
import { useExportImport } from '../hooks/useExportImport';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { date: '', share_value: '', total_shares: '', total_value: '', notes: '' };

export default function VamStatsPage() {
  const { currentCompany } = useCompany();
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const { handleExportCSV, handleImportCSV, exporting, importing } = useExportImport({
    exportUrl: '/api/vam-stats/export/csv',
    importUrl: '/api/vam-stats/import/csv',
    companyId: currentCompany?.id,
    onImportSuccess: () => fetchStats(),
    entityName: 'vam_stats'
  });

  useEffect(() => {
    if (currentCompany) fetchStats();
  }, [currentCompany]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/vam-stats`, { params: { company_id: currentCompany.id }, withCredentials: true });
      setStats(res.data);
    } catch { toast.error('Failed to load VAM stats'); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!form.date) { toast.error('Date is required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        company_id: currentCompany.id,
        date: form.date,
        share_value: form.share_value ? parseFloat(form.share_value) : null,
        total_shares: form.total_shares ? parseFloat(form.total_shares) : null,
        total_value: form.total_value ? parseFloat(form.total_value) : null,
        notes: form.notes || null
      };
      await axios.post(`${API_URL}/api/vam-stats`, payload, { withCredentials: true });
      toast.success('Entry added');
      setIsAddOpen(false);
      setForm(EMPTY);
      fetchStats();
    } catch { toast.error('Failed to add'); }
    finally { setSubmitting(false); }
  };

  const handleEdit = async () => {
    if (!form.date) { toast.error('Date is required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        date: form.date,
        share_value: form.share_value ? parseFloat(form.share_value) : null,
        total_shares: form.total_shares ? parseFloat(form.total_shares) : null,
        total_value: form.total_value ? parseFloat(form.total_value) : null,
        notes: form.notes || null
      };
      await axios.put(`${API_URL}/api/vam-stats/${selected.id}`, payload, { withCredentials: true });
      toast.success('Entry updated');
      setIsEditOpen(false);
      setSelected(null);
      setForm(EMPTY);
      fetchStats();
    } catch { toast.error('Failed to update'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/api/vam-stats/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Entry deleted');
      setDeleteTarget(null);
      fetchStats();
    } catch { toast.error('Failed to delete'); }
  };

  const openEdit = (s) => {
    setSelected(s);
    setForm({ date: s.date, share_value: s.share_value?.toString() || '', total_shares: s.total_shares?.toString() || '', total_value: s.total_value?.toString() || '', notes: s.notes || '' });
    setIsEditOpen(true);
  };

  const openAdd = () => {
    const today = new Date().toISOString().split('T')[0];
    setForm({ ...EMPTY, date: today });
    setIsAddOpen(true);
  };

  const formatCurrency = (v) => v != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v) : '-';
  const formatNumber = (v) => v != null ? v.toLocaleString() : '-';

  // Summary cards
  const latest = stats[0];
  const prev = stats[1];
  const shareValueChange = (latest?.share_value != null && prev?.share_value != null) ? ((latest.share_value - prev.share_value) / prev.share_value * 100) : null;

  if (!currentCompany) return <div className="flex items-center justify-center h-64"><p className="text-gray-500">Please select a company</p></div>;

  const statForm = (
    <div className="space-y-4 py-2">
      <div className="space-y-2"><Label>Date *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="vam-date-input" /></div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Share Value ($)</Label><Input type="number" step="0.01" value={form.share_value} onChange={(e) => setForm({ ...form, share_value: e.target.value })} placeholder="0.00" data-testid="vam-share-value-input" /></div>
        <div className="space-y-2"><Label>Total Shares</Label><Input type="number" step="1" value={form.total_shares} onChange={(e) => setForm({ ...form, total_shares: e.target.value })} placeholder="0" /></div>
      </div>
      <div className="space-y-2"><Label>Total Value ($)</Label><Input type="number" step="0.01" value={form.total_value} onChange={(e) => setForm({ ...form, total_value: e.target.value })} placeholder="0.00" /></div>
      <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Additional notes..." /></div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="vam-stats-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">VAM Stats</h1>
          <p className="text-gray-500 mt-1">Track daily share values and portfolio data</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportImportButtons onExportCSV={handleExportCSV} onImportCSV={handleImportCSV} exporting={exporting} importing={importing} testIdPrefix="vam-stats" />
          <Button onClick={openAdd} data-testid="add-vam-stat-btn"><Plus className="w-4 h-4 mr-2" />Add Entry</Button>
        </div>
      </div>

      {/* Summary Cards */}
      {latest && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm text-gray-500 uppercase tracking-wide">Latest Share Value</p>
            <div className="flex items-end gap-2 mt-1">
              <p className="text-2xl font-semibold">{formatCurrency(latest.share_value)}</p>
              {shareValueChange !== null && (
                <span className={`flex items-center text-xs font-medium pb-1 ${shareValueChange >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  {shareValueChange >= 0 ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  {Math.abs(shareValueChange).toFixed(2)}%
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">{latest.date}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm text-gray-500 uppercase tracking-wide">Total Shares</p>
            <p className="text-2xl font-semibold mt-1">{formatNumber(latest.total_shares)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm text-gray-500 uppercase tracking-wide">Total Value</p>
            <p className="text-2xl font-semibold mt-1">{formatCurrency(latest.total_value)}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : stats.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No data yet</h3>
          <p className="text-gray-500 mb-4">Add your first share value entry</p>
          <Button onClick={openAdd}><Plus className="w-4 h-4 mr-2" />Add Entry</Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Share Value</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Shares</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Value</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                <th className="px-4 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {stats.map((s, i) => {
                const prevStat = stats[i + 1];
                const change = (s.share_value != null && prevStat?.share_value != null) ? s.share_value - prevStat.share_value : null;
                return (
                  <tr key={s.id} className="hover:bg-gray-50" data-testid={`vam-stat-row-${s.id}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">{s.date}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className="font-medium text-gray-900">{formatCurrency(s.share_value)}</span>
                        {change !== null && (
                          <span className={`text-xs font-medium ${change >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                            {change >= 0 ? '+' : ''}{change.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">{formatNumber(s.total_shares)}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{formatCurrency(s.total_value)}</td>
                    <td className="px-4 py-3 text-gray-500 text-sm max-w-xs truncate">{s.notes || '-'}</td>
                    <td className="px-4 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(s)}><Pencil className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDeleteTarget(s)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent data-testid="add-vam-stat-dialog"><DialogHeader><DialogTitle>Add Entry</DialogTitle><DialogDescription>Record a new share value.</DialogDescription></DialogHeader>{statForm}<DialogFooter><Button variant="outline" onClick={() => setIsAddOpen(false)}>Cancel</Button><Button onClick={handleAdd} disabled={submitting} data-testid="save-vam-stat-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}</Button></DialogFooter></DialogContent>
      </Dialog>
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent><DialogHeader><DialogTitle>Edit Entry</DialogTitle></DialogHeader>{statForm}<DialogFooter><Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button><Button onClick={handleEdit} disabled={submitting}>{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button></DialogFooter></DialogContent>
      </Dialog>
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Entry</AlertDialogTitle><AlertDialogDescription>Delete entry for {deleteTarget?.date}?</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
